import { useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { X, Plus } from 'lucide-react';

export interface WebsitePathsEditorProps {
  paths: string[];
  onChange: (paths: string[]) => void;
  disabled?: boolean;
}

/**
 * Component for managing website paths in campaigns.
 * Allows users to add and remove website paths with validation.
 */
export function WebsitePathsEditor({ paths, onChange, disabled = false }: WebsitePathsEditorProps) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const validatePath = (path: string): string | null => {
    if (!path.startsWith('/')) {
      return 'Path must start with /';
    }
    if (/\s/.test(path)) {
      return 'Path cannot contain spaces';
    }
    if (path.length < 2) {
      return 'Path is too short';
    }
    // Check for duplicate paths (case-insensitive)
    if (paths.some(existingPath => existingPath.toLowerCase() === path.toLowerCase())) {
      return 'This path already exists';
    }
    return null;
  };

  const handleAdd = () => {
    const trimmedPath = inputValue.trim();
    if (!trimmedPath) {
      setError('Please enter a path');
      return;
    }

    const validationError = validatePath(trimmedPath);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Add the path and clear input
    onChange([...paths, trimmedPath]);
    setInputValue('');
    setError(null);
  };

  const handleRemove = (pathToRemove: string) => {
    onChange(paths.filter(path => path !== pathToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  return (
    <div className="space-y-3">
      {/* Input section */}
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            type="text"
            placeholder="/about/careers/"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={error ? 'border-red-500' : ''}
          />
          {error && (
            <div className="text-xs text-red-600 mt-1">{error}</div>
          )}
        </div>
        <Button
          type="button"
          onClick={handleAdd}
          disabled={disabled || !inputValue.trim()}
          className="shrink-0"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>

      {/* Help text */}
      <div className="text-xs text-gray-500">
        Enter website paths that start with / (e.g., /about/careers/, /products/bakery/)
      </div>

      {/* Paths list */}
      {paths.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">
            {paths.length} path{paths.length !== 1 ? 's' : ''} added:
          </div>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {paths.map((path, index) => (
              <div
                key={`${path}-${index}`}
                className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm"
              >
                <span className="font-mono text-blue-600 truncate mr-2">{path}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(path)}
                  disabled={disabled}
                  className="h-6 w-6 p-0 text-gray-500 hover:text-red-600"
                  aria-label={`Remove ${path}`}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {paths.length === 0 && (
        <div className="text-sm text-gray-500 italic">
          No website paths added yet.
        </div>
      )}
    </div>
  );
}